import React, {useCallback, useEffect, useLayoutEffect, useState} from 'react';
import {Platform, TouchableOpacity, View} from 'react-native';
import {ScrollView} from 'react-native-gesture-handler';
import {StyleSheet} from 'react-native';
import {colorPalette, colors} from '../../../core/config/color.config';

import {RNEInput, RNEText, SVGIcon} from '../../components';

import {
  useFocusEffect,
  useNavigation,
  useRoute,
} from '@react-navigation/native';

import {
  ConsultationHistoryTab,
  ConsultationOngoingTab,
  ConsultationPendingTab,
  ConsultationRecentlyViewedAccount,
} from '../../views';

import {useDebounce} from '../../hooks/debounce/useDebounce';
import useStores from '../../stores/useStores';
import {NavigationKey} from '../../navigations/config';
import {usePullDownRefreshContext} from '../../stores/pullDownRefreshStore';

const ConsultationSearchResultsScreen = () => {
  const navigation = useNavigation();
  const route = useRoute();

  const {recentlyViewedAccountsStore} = useStores();

  const [searchValue, setSearchValue] = useState('');
  const [routeParams, setRouteParams] = useState({});
  const [currentTab, setCurrentTab] = useState('ongoing');

  const debouncedSearchValue = useDebounce(searchValue, 350);

  const goBack = () => {
    setSearchValue('');
    navigation.goBack();
  };

  const onItemPressed = (id: string) => {
    const params = {
      ...routeParams,
      id: id,
    };
    navigation.navigate(NavigationKey.CONSULTATION_DETAIL, params);
  };

  const displayTab = useCallback(() => {
    switch (currentTab) {
      case 'ongoing':
        return <ConsultationOngoingTab searchKey={debouncedSearchValue} />;

      case 'pending':
        return <ConsultationPendingTab searchKey={debouncedSearchValue} />;

      case 'history':
        return <ConsultationHistoryTab searchKey={debouncedSearchValue} />;
    }
  }, [currentTab, debouncedSearchValue]);

  useEffect(() => {
    const params: any = route.params;
    if (params) {
      setRouteParams(params);

      if (params.currentTab) {
        setCurrentTab(params.currentTab);
      }
    }
  }, [route]);

  useLayoutEffect(() => {
    if (Platform.OS !== 'web') {
      navigation.setOptions({
        animationEnabled: false,
        header: () => {
          null;
        },
        // header: () => (
        //   <View style={styles.headerContainer}>
        //     <RNEInput
        //       value={searchValue}
        //       onChangeText={setSearchValue}
        //       placeholder="Search"
        //       placeholderTextColor={colorPalette.grey}
        //       inputContainerStyle={inputContainerStyle}
        //       containerStyle={containerStyle}
        //       autoFocus={true}
        //       returnKeyType="search"
        //       leftIcon={
        //         <TouchableOpacity onPress={goBack}>
        //           <SVGIcon name="left-black" size={20} />
        //         </TouchableOpacity>
        //       }
        //     />
        //   </View>
        // ),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigation, searchValue]);

  const {setCanRefresh} = usePullDownRefreshContext();

  const [scrollViewHeight, setScrollViewHeight] = useState(0);
  const [contentHeight, setContentHeight] = useState(0);
  const [scrollY, setScrollY] = useState(0);

  useFocusEffect(
    useCallback(() => {
      if (scrollViewHeight > 0 && contentHeight > 0) {
        const isScrollable = contentHeight > scrollViewHeight;

        if (!isScrollable) {
          setCanRefresh(true); // always allow pull-down if not scrollable
        } else {
          setCanRefresh(scrollY <= 0); // only allow when scrolled to top
        }
      }
    }, [scrollViewHeight, contentHeight, scrollY, setCanRefresh]),
  );
  const handleScroll = (e: any) => {
    const y = e.nativeEvent.contentOffset.y;
    setScrollY(y);

    const isScrollable = contentHeight > scrollViewHeight;

    if (!isScrollable) {
      setCanRefresh(true); // still always true if no scroll
    } else {
      setCanRefresh(y <= 0); // only true when back at the very top
    }
  };

  return (
    <ScrollView
      onScroll={handleScroll}
      onLayout={e => {
        setScrollViewHeight(e.nativeEvent.layout.height);
      }}
      onContentSizeChange={(contentWidth, contentHeight) => {
        setContentHeight(contentHeight);
      }}>
      <View style={styles.headerContainer}>
        <RNEInput
          value={searchValue}
          onChangeText={setSearchValue}
          placeholder="Search"
          placeholderTextColor={colorPalette.grey}
          inputContainerStyle={inputContainerStyle}
          containerStyle={containerStyle}
          autoFocus={true}
          returnKeyType="search"
          leftIcon={
            <TouchableOpacity onPress={goBack}>
              <SVGIcon name="left-black" size={20} />
            </TouchableOpacity>
          }
        />
      </View>
      <View style={styles.mainContainer}>
        {!searchValue ? (
          <View style={styles.recentContainer}>
            {recentlyViewedAccountsStore.recentlyViewedAccts.length > 0 && (
              <>
                <RNEText label="Recent" weight="bold" />
                {recentlyViewedAccountsStore.recentlyViewedAccts.map(
                  (account, index) => (
                    <ConsultationRecentlyViewedAccount
                      key={index}
                      item={account}
                      onPress={() => onItemPressed(account.basedOnId)}
                    />
                  ),
                )}
              </>
            )}
          </View>
        ) : (
          <>{displayTab()}</>
        )}
      </View>
    </ScrollView>
  );
};

const inputContainerStyle = {
  borderWidth: 0,
  borderBottomWidth: 0,
};

const containerStyle = {
  width: '80%',
};

const styles = StyleSheet.create({
  headerContainer: {
    // marginHorizontal: 20,
    marginTop: Platform.OS === 'ios' ? '10%' : 0,
    borderBottomColor: colors.gray.lighter,
    borderBottomWidth: 1,
    height: 60,
    justifyContent: 'center',
  },
  mainContainer: {
    marginHorizontal: 20,
  },
  recentContainer: {
    marginTop: 32,
  },
});

export default ConsultationSearchResultsScreen;
